import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCopy, FiCheck, FiMusic, FiShieldOff, FiAlignLeft, FiTag } = FiIcons;

const CopyBox = ({ title, content, icon, delay }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!content) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200"
    >
      <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-4">
        <h3 className="font-bold text-stone-800 flex items-center gap-2">
          <SafeIcon icon={icon} className="text-orange-500" />
          {title}
        </h3>
        <button
          onClick={handleCopy}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            copied 
              ? 'bg-green-50 text-green-600' 
              : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
          }`}
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-stone-600 text-sm leading-relaxed">
        {content}
      </pre>
    </motion.div>
  );
};

const TitlePill = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 border text-left
        ${copied 
          ? 'bg-green-50 border-green-200 text-green-700 shadow-sm' 
          : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800 shadow-sm hover:shadow'
        }`}
    >
      <span className="flex-1">{text}</span>
      <SafeIcon 
        icon={copied ? FiCheck : FiCopy} 
        className={copied ? 'text-green-500' : 'text-stone-400'} 
      />
    </button>
  );
};

const TitleSuggestions = ({ titles, delay }) => {
  if (!titles || titles.length === 0) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200"
    >
      <div className="flex justify-between items-center mb-4 border-b border-stone-100 pb-4">
        <div>
          <h3 className="font-bold text-stone-800 flex items-center gap-2">
            <SafeIcon icon={FiTag} className="text-orange-500" />
            Suggested Titles
          </h3>
          <p className="text-xs text-stone-500 mt-1 ml-6">Click any title to copy it</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {titles.map((title, index) => (
          <TitlePill key={index} text={title} />
        ))}
      </div>
    </motion.div>
  );
};

const CreatorResults = ({ result }) => {
  if (!result) return null;

  return (
    <div className="w-full space-y-6 mt-8 pb-8">
      {result.titles && (
        <TitleSuggestions titles={result.titles} delay={0.05} />
      )}
      
      <CopyBox 
        title="Style Prompt" 
        content={result.stylePrompt} 
        icon={FiMusic} 
        delay={0.1} 
      />
      
      {result.excludePrompt && (
        <CopyBox 
          title="Exclude Styles (Negative Prompt)" 
          content={result.excludePrompt} 
          icon={FiShieldOff} 
          delay={0.2} 
        />
      )}
      
      {result.lyrics && (
        <CopyBox 
          title="Custom Lyrics" 
          content={result.lyrics} 
          icon={FiAlignLeft} 
          delay={0.3} 
        />
      )}
    </div>
  );
};

export default CreatorResults;